import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";

import { useT } from "@/i18n";
import { toEmbedSrc, embedKind } from "@/lib/embeds";
import { artistDisciplines } from "@/lib/artist";
import Seo from "@/components/Seo";
import EventsListHover from "@/components/EventsListHover";
import { sectionScheme, schemeHex } from "@/lib/schemes";
import { useSiteContent } from "@/context/SiteContentContext";

export default function ArtistDetail() {
    const { lang } = useT();
    const { slug } = useParams();
    const { content: siteContent } = useSiteContent();
    const [artist, setArtist] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [products, setProducts] = useState([]);
    const [events, setEvents] = useState([]);

    // Admin-controlled visibility for the header meta line. Defaults visible.
    const showHeroDiscipline = siteContent.artist_detail_show_discipline !== "0";
    const showHeroLocation = siteContent.artist_detail_show_location !== "0";

    useEffect(() => {
        api.get(`/artists/${slug}`)
            .then((r) => setArtist(r.data))
            .catch(() => setNotFound(true));
        // Pull all products + events once — filter client-side against artist_slug / lineup.
        api.get("/products").then((r) => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => setProducts([]));
        api.get("/events").then((r) => setEvents(Array.isArray(r.data) ? r.data : [])).catch(() => setEvents([]));
    }, [slug]);

    // Products credited to this artist (via legacy artist_slug OR new artist_slugs[]).
    const artistProducts = useMemo(() => {
        if (!artist) return [];
        return products.filter((p) => {
            if (p.artist_slug === artist.slug) return true;
            const arr = Array.isArray(p.artist_slugs) ? p.artist_slugs : [];
            return arr.includes(artist.slug);
        });
    }, [products, artist]);

    // Events where this artist appears in the lineup — case-insensitive name match.
    const artistEvents = useMemo(() => {
        if (!artist) return [];
        const targetName = (artist.name || "").trim().toLowerCase();
        const nowIso = new Date().toISOString();
        return events
            .filter((ev) => {
                const line = Array.isArray(ev.lineup) ? ev.lineup : [];
                return line.some((n) => (n || "").trim().toLowerCase() === targetName);
            })
            .sort((a, b) => {
                // Upcoming first (sorted ascending), then past (sorted descending).
                const ap = (a.date || "") >= nowIso;
                const bp = (b.date || "") >= nowIso;
                if (ap !== bp) return ap ? -1 : 1;
                return ap
                    ? (a.date || "").localeCompare(b.date || "")
                    : (b.date || "").localeCompare(a.date || "");
            });
    }, [events, artist]);

    if (notFound)
        return (
            <div className="p-16 min-h-[60vh] flex flex-col items-center justify-center">
                <div className="font-heading text-4xl uppercase">not found.</div>
                <Link to="/artists" className="mt-6 font-mono text-xs uppercase tracking-[0.2em] link-underline">
                    ← back to roster
                </Link>
            </div>
        );
    if (!artist) return <div className="p-16 font-mono text-xs uppercase tracking-[0.3em]">loading…</div>;

    // Per-artist page scheme — the artist's own "room". Overrides the /artists
    // default page scheme so each artist's detail page can have its own accent world.
    const artistScheme = (artist.scheme && artist.scheme !== "") ? sectionScheme(artist.scheme) : null;
    const artistHex = (artist.scheme && artist.scheme !== "") ? schemeHex(artist.scheme) : null;

    return (
        <div
            data-testid="artist-detail-page"
            className={artistScheme ? `${artistScheme.bg} ${artistScheme.text}` : ""}
            style={artistHex ? {
                "--sc-bg": artistHex.bg,
                "--sc-text": artistHex.text,
                "--sc-subtext": artistHex.subtext,
                "--sc-accent": artistHex.accent,
                "--sc-accent2": artistHex.accent2,
                "--sc-border": artistHex.border,
                "--sc-card": artistHex.card,
            } : {}}
        >
            <Seo
                title={`${artist.name} — ${artistDisciplines(artist).join(" · ") || artist.discipline || "artist"} | ITZKATU`}
                description={(artist.short_description || artist.bio || `${artist.name}, ${artist.discipline} del colectivo itzkatu.`).slice(0, 160)}
                image={artist.image_url || artist.cover_url || ""}
                path={`/artists/${artist.slug}`}
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Person",
                    name: artist.name,
                    url: `${window.location.origin}/artists/${artist.slug}`,
                    image: artist.image_url || undefined,
                    description: (artist.short_description || artist.bio || "").slice(0, 200) || undefined,
                    jobTitle: artist.discipline || undefined,
                    memberOf: { "@type": "Organization", name: "ITZKATU" },
                    sameAs: Object.values(artist.socials || {}).filter(Boolean),
                }}
            />
            {/* HERO — narrow centered column. No huge portrait block on the right — the work below tells the story.
                Discipline is italic, location is regular (non-uppercase). Admin can toggle each field on/off
                via `artist_detail_show_discipline` and `artist_detail_show_location`. */}
            <section className="px-4 md:px-8 pt-16 md:pt-24 pb-8 md:pb-12">
                <div className="max-w-2xl mx-auto text-center">
                    {(showHeroDiscipline || showHeroLocation) && (
                        <div className="font-body text-sm md:text-base opacity-70 space-y-0.5">
                            {showHeroDiscipline && (
                                <div className="italic">
                                    {artistDisciplines(artist).join(" · ") || artist.discipline}
                                </div>
                            )}
                            {showHeroLocation && artist.location && (
                                <div className="opacity-80">{artist.location}</div>
                            )}
                        </div>
                    )}
                    <h1 className="mt-3 font-heading font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9] text-current">
                        {artist.name}
                    </h1>
                    {artist.bio && (
                        <p className="mt-6 font-body text-base leading-relaxed max-w-[60ch] mx-auto whitespace-pre-line opacity-80">
                            {artist.bio}
                        </p>
                    )}
                    <div className="mt-8 flex flex-wrap justify-center gap-4">
                        {Object.entries(artist.socials || {}).map(([k, v]) =>
                            v ? (
                                <a
                                    key={k}
                                    href={v}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`social-${k}`}
                                    className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-70 hover:text-[color:var(--sc-accent)] hover:opacity-100 underline decoration-1 underline-offset-4 transition-colors inline-flex items-center gap-1"
                                >
                                    {k} <ArrowUpRight size={11} />
                                </a>
                            ) : null
                        )}
                    </div>
                </div>
            </section>

            {/* PORTRAIT — narrow-centered, not full-bleed, breathing space around it */}
            {artist.image_url && (
                <section className="px-4 md:px-8 pb-6 md:pb-10">
                    <div className="max-w-2xl mx-auto">
                        <img
                            src={artist.image_url_alt || artist.image_url}
                            alt={artist.name}
                            className="w-full h-auto object-cover"
                            referrerPolicy="no-referrer"
                        />
                    </div>
                </section>
            )}

            {/* LISTEN / WATCH — every embed picks the aspect that fits its
                platform: Spotify track = short strip, Spotify album/playlist
                = tall, Instagram post = tall square, YouTube/Vimeo = 16:9,
                everything else = 4:3. That way nothing gets clipped and users
                don't need to scroll inside the iframe. */}
            {artist.embeds && artist.embeds.length > 0 && (
                <section className="px-4 md:px-8 py-8 md:py-12">
                    <div className="max-w-3xl mx-auto space-y-8">
                        {artist.embeds.map((raw, i) => {
                            const src = toEmbedSrc(raw);
                            const kind = embedKind(raw);
                            const isInstagram = /instagram\.com/i.test(raw);
                            const isSpotifyAlbum = /spotify\.com\/(album|playlist)/i.test(raw);
                            const isSpotifyTrack = /spotify\.com\/(track|episode)/i.test(raw);
                            // Instagram + Spotify album/playlist need tall containers.
                            // Spotify track = fixed 232 px (short strip).
                            // Everything else falls back to 16:9.
                            let containerCls = "aspect-video";
                            let containerStyle = {};
                            if (isSpotifyTrack) {
                                containerStyle = { height: "232px" };
                                containerCls = "";
                            } else if (isSpotifyAlbum) {
                                containerStyle = { height: "500px" };
                                containerCls = "";
                            } else if (isInstagram) {
                                // IG default post embed needs ~880 px to show full post + caption.
                                containerStyle = { height: "880px" };
                                containerCls = "";
                            } else if (kind === "audio") {
                                containerStyle = { height: "232px" };
                                containerCls = "";
                            }
                            // Vary widths for rhythm
                            const wideness = i % 3 === 0 ? "max-w-3xl" : i % 3 === 1 ? "max-w-xl" : "max-w-2xl";
                            return (
                                <div key={i} className={`mx-auto ${wideness}`}>
                                    <div
                                        className={`${containerCls} bg-transparent overflow-hidden`}
                                        style={containerStyle}
                                    >
                                        <iframe
                                            src={src}
                                            title={`embed-${i}`}
                                            className="w-full h-full block"
                                            frameBorder="0"
                                            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                            scrolling="no"
                                            loading="lazy"
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* WORKS — flexible vertical gallery, alternating widths */}
            {artist.works && artist.works.length > 0 && (
                <section className="px-4 md:px-8 py-8 md:py-12">
                    <div className="max-w-4xl mx-auto space-y-8">
                        {artist.works.map((w, i) => {
                            const wideness = i % 3 === 0 ? "max-w-3xl" : i % 3 === 1 ? "max-w-md" : "max-w-2xl";
                            return (
                                <div key={i} className={`mx-auto ${wideness}`}>
                                    <img src={w} alt="work" loading="lazy" className="w-full h-auto object-cover" />
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* SHOP — quiet, integrated. Uses same visual language as homepage. */}
            {artistProducts.length > 0 && (
                <section data-testid="artist-shop" className="px-4 md:px-8 py-12 md:py-16">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center font-mono text-[10px] uppercase tracking-[0.35em] opacity-60 mb-8">
                            {lang === "es" ? "objetos" : "objects"}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-8 md:gap-x-8 md:gap-y-12">
                            {artistProducts.slice(0, 6).map((p) => (
                                <a
                                    key={p.id}
                                    href={p.etsy_url || p.shopify_url || "#"}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`artist-product-${p.id}`}
                                    className="group flex flex-col"
                                >
                                    <div className="aspect-square bg-[color:var(--sc-card)] overflow-hidden">
                                        {p.image_url && (
                                            <img src={p.image_url} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-80" referrerPolicy="no-referrer" />
                                        )}
                                    </div>
                                    <div className="mt-2.5 font-heading font-medium text-sm truncate">{p.title}</div>
                                    {typeof p.price === "number" && (
                                        <div className="mt-0.5 font-body italic text-xs opacity-60">
                                            {p.currency === "EUR" ? "€" : "$"}{Number(p.price).toFixed(2)}
                                        </div>
                                    )}
                                </a>
                            ))}
                        </div>
                        <div className="mt-10 text-center">
                            <Link to="/shop" className="font-mono text-[10px] uppercase tracking-[0.3em] underline decoration-1 underline-offset-4 hover:text-[color:var(--sc-accent)] transition-colors">
                                full shop →
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* EVENTS — appearances in the /events row format (thumb + hover flyer) */}
            {artistEvents.length > 0 && (
                <section data-testid="artist-events" className="px-4 md:px-8 py-12 md:py-16">
                    <div className="max-w-6xl mx-auto">
                        <div className="font-mono text-xs uppercase tracking-[0.35em] text-[color:var(--sc-accent)] mb-6">
                            {lang === "es" ? "apariciones" : "appearances"}
                        </div>
                        <EventsListHover events={artistEvents.slice(0, 8)} />
                    </div>
                </section>
            )}

            <section className="px-4 md:px-8 py-10 text-center">
                <Link to="/artists" data-testid="back-to-roster" className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-70 hover:opacity-100 transition-opacity">
                    ← back to roster
                </Link>
            </section>
        </div>
    );
}
